import { useEffect, useState } from "react";
import { Container, Card, Button, Table, Modal, Form, Row, Col, Badge } from "react-bootstrap";
//Components 
import NavBar from '../NavBar/Navbar';
import AssignProductsModal from "./AssignProductsModal";
//API
import { PickNGoFetchAPI } from "../../api/PickAndGo";
//Utils
import { RenderIf } from "../utils/RenderIf";
//Alerts
import SuccessAlert from "../Alerts/SuccessAlert";
import ErrorAlert from "../Alerts/ErrorAlert";

const EMPTY = {
    id: null, business_id: "", branchoffice_id: null,
    slot_minutes: 30, window_start: "8:00", window_end: "17:00",
    days_ahead: 7, lead_time_minutes: 60, is_active: true, priority: 100,
    type: "all",
    limit_mode: "days_ahead",
    start_date: "",
    end_date: ""
};

const PickAndGoManager = () => {
    const [rows, setRows] = useState([]);
    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);
    const business = JSON.parse(localStorage.getItem('user'))?.business || {};
    const token = localStorage.getItem('x-access-token');
    const branches = JSON.parse(localStorage.getItem('user'))?.branchoffices || [];
    const [showProducts, setShowProducts] = useState(false);
    const [ruleForProducts, setRuleForProducts] = useState(null);
    /**
     * Load the pick and go rules for the business
     */
    const getPickAndGoRulesByBusiness = async () => {
        try {
            const res = await PickNGoFetchAPI.getPickAndGoRulesByBusiness(business.id, token);
            console.log("Pick & Go rules", res);
            const data = res?.data?.rules || res?.data || [];
            setRows(Array.isArray(data) ? data : []);
        } catch (e) { ErrorAlert("No se pudo cargar", "Pick & Go", "warning"); }
    };
    /**
     * Hook to load the pick and go rules on component mount
     */
    useEffect(() => { getPickAndGoRulesByBusiness(); }, []);

    const openCreate = () => { setEditing({ ...EMPTY, business_id: business.id }); setShow(true); };
    const openEdit = (r) => {
        setEditing({
            ...EMPTY, ...r, is_active: !!r.is_active,
            limit_mode: r.limit_mode || "days_ahead",
            start_date: r.start_date || "",
            end_date: r.end_date || "",
            days_ahead: r.days_ahead ?? 7
        }); setShow(true);
    };

    const save = async () => {
        try {
            const payload = {
                ...editing,
                business_id: Number(editing.business_id),
                name: editing.name?.trim() || 'Sin nombre',
                branchoffice_id: editing.branchoffice_id ? Number(editing.branchoffice_id) : null,
                slot_minutes: Number(editing.slot_minutes),
                lead_time_minutes: Number(editing.lead_time_minutes),
                is_active: editing.is_active ? 1 : 0,
                priority: editing.priority ? Number(editing.priority) : 100,
                type: editing.type || "all",
                limit_mode: editing.limit_mode || "days_ahead",
                days_ahead: (editing.limit_mode || "days_ahead") === "days_ahead"
                    ? Number(editing.days_ahead)
                    : null,
                start_date: (editing.limit_mode || "days_ahead") === "date_range"
                    ? editing.start_date || null
                    : null,
                end_date: (editing.limit_mode || "days_ahead") === "date_range"
                    ? editing.end_date || null
                    : null
            };
            if (editing.id) await PickNGoFetchAPI.updatePickAndGoRule(editing.id, payload, token);
            else await PickNGoFetchAPI.createPickAndGoRule(payload, token);
            SuccessAlert("Configuración guardada", "Listo");
            setShow(false);
            // We reload the rules
            getPickAndGoRulesByBusiness();
        } catch (e) { ErrorAlert(e?.response?.data?.error || "Error guardando", "Pick & Go", "warning"); }
    };

    const openProducts = (r) => {
        setRuleForProducts({ id: r.id, name: r.name || `Regla #${r.id}` });
        setShowProducts(true);
    };

    return (
        <>
            {/** Navbar */}
            <NavBar titlePage="Pick & Go Manager" route="config/new_pick-and-go" />
            {/**Content */}
            <Container className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Pick & Go – Configuración</h4>
                    <Button onClick={openCreate}>+ Nueva</Button>
                </div>
                <Card><Card.Body>
                    <Table size="sm" hover>
                        <thead>
                            <tr>
                                <th>Activa</th>
                                <th>Nombre</th>
                                <th>Sucursal</th>
                                <th>Duración de cada ventana (minutos)</th>
                                <th>Horario de disponibilidad</th>
                                <th>Límite de reserva</th>
                                <th>Anticipación mínima para pedir (minutos)</th>
                                <th>Prioridad</th>
                                <th>Tipo de regla</th>
                                <th>Modo de límite</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td>{Number(r.is_active) ? <Badge bg="success">Sí</Badge> : <Badge bg="secondary">No</Badge>}</td>
                                    <td>{r.name}</td>
                                    <td>{r.branchoffice_name ?? "General negocio"}</td>
                                    <td>{r.slot_minutes}</td>
                                    <td>{r.window_start}–{r.window_end}</td>
                                    <td>
                                        {r.limit_mode === "date_range"
                                            ? `${r.start_date?.slice(0, 10) || '-'} a ${r.end_date?.slice(0, 10) || '-'}`
                                            : `${r.days_ahead} día(s)`}
                                    </td>
                                    <td>{r.lead_time_minutes}</td>
                                    <td>{r.priority}</td>
                                    <td>{r.type === "all" ? "Toda la orden" : "Solo productos asignados"}</td>
                                    <td>{r.limit_mode === "date_range" ? "Rango de fechas" : "Días a futuro"}</td>
                                    <td className="text-end">
                                        <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>Editar</Button>
                                        <RenderIf isTrue={r.type === "product"}>
                                            <Button size="sm" variant="outline-primary" onClick={() => openProducts(r)}>Agregar productos</Button>
                                        </RenderIf>
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={7} className="text-center text-muted">Sin configuraciones</td></tr>}
                        </tbody>
                    </Table>
                </Card.Body></Card>
                {/**Modal to create and edit configurations */}
                <Modal show={show} onHide={() => setShow(false)} centered>
                    <Modal.Header closeButton><Modal.Title>{editing?.id ? 'Editar' : 'Nueva'} Configuración</Modal.Title></Modal.Header>
                    <Modal.Body>
                        {editing && (
                            <Form>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Label>Sucursal</Form.Label>
                                        <Form.Select
                                            value={editing.branchoffice_id ?? 'null'}
                                            onChange={e => setEditing({ ...editing, branchoffice_id: e.target.value === 'null' ? null : e.target.value })}
                                        >
                                            <option value="null">General del negocio</option>
                                            {branches.map(b => (
                                                <option key={b.branchoffice_id} value={b.branchoffice_id}>{b.branchoffice_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Label>Nombre</Form.Label>
                                        <Form.Control type="text" value={editing.name || ''}
                                            onChange={e => setEditing({ ...editing, name: e.target.value })} />
                                    </Col>

                                    <Col md={4}>
                                        <Form.Label>Lapso (min)</Form.Label>
                                        <Form.Control type="number" value={editing.slot_minutes}
                                            onChange={e => setEditing({ ...editing, slot_minutes: e.target.value })} />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Desde</Form.Label>
                                        <Form.Control type="time" value={editing.window_start}
                                            onChange={e => setEditing({ ...editing, window_start: e.target.value })} />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Hasta</Form.Label>
                                        <Form.Control type="time" value={editing.window_end}
                                            onChange={e => setEditing({ ...editing, window_end: e.target.value })} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Modo de límite</Form.Label>
                                        <Form.Select
                                            value={editing.limit_mode || 'days_ahead'}
                                            onChange={e =>
                                                setEditing({
                                                    ...editing,
                                                    limit_mode: e.target.value,
                                                    days_ahead: e.target.value === "days_ahead" ? (editing.days_ahead || 7) : "",
                                                    start_date: e.target.value === "date_range" ? (editing.start_date || "") : "",
                                                    end_date: e.target.value === "date_range" ? (editing.end_date || "") : ""
                                                })
                                            }
                                        >
                                            <option value="days_ahead">Días a futuro</option>
                                            <option value="date_range">Rango de fechas</option>
                                        </Form.Select>
                                    </Col>
                                    <RenderIf isTrue={(editing.limit_mode || "days_ahead") === "days_ahead"}>
                                        <Col md={6}>
                                            <Form.Label>Días a futuro</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editing.days_ahead}
                                                onChange={e => setEditing({ ...editing, days_ahead: e.target.value })}
                                            />
                                        </Col>
                                    </RenderIf>

                                    <RenderIf isTrue={(editing.limit_mode || "days_ahead") === "date_range"}>
                                        <>
                                            <Col md={6}>
                                                <Form.Label>Fecha inicio</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={editing.start_date || ""}
                                                    onChange={e => setEditing({ ...editing, start_date: e.target.value })}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label>Fecha fin</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={editing.end_date || ""}
                                                    onChange={e => setEditing({ ...editing, end_date: e.target.value })}
                                                />
                                            </Col>
                                        </>
                                    </RenderIf>
                                    <Col md={6}>
                                        <Form.Label>Tiempo preparación (min)</Form.Label>
                                        <Form.Control type="number" value={editing.lead_time_minutes}
                                            onChange={e => setEditing({ ...editing, lead_time_minutes: e.target.value })} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Nivel de prioridad</Form.Label>
                                        <Form.Control type="number" value={editing.priority}
                                            onChange={e => setEditing({ ...editing, priority: e.target.value })} />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Label>Tipo de regla</Form.Label>
                                        <Form.Select
                                            value={editing.type || 'all'}
                                            onChange={e => setEditing({ ...editing, type: e.target.value })}
                                        >
                                            <option value="all">Todos los productos</option>
                                            <option value="product">Solo productos asignados</option>
                                        </Form.Select>
                                    </Col>
                                    <RenderIf isTrue={!!editing.id}>
                                        <Col md={12}>
                                            <Form.Check type="switch" label="Activa"
                                                checked={!!editing.is_active}
                                                onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                                        </Col>
                                    </RenderIf>
                                </Row>
                            </Form>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShow(false)}>Cancelar</Button>
                        <Button variant="primary" onClick={save}>Guardar</Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal of asign products */}
                <AssignProductsModal
                    show={showProducts}
                    onHide={() => setShowProducts(false)}
                    rule={ruleForProducts}
                    businessId={business?.id}
                    token={token}
                />
            </Container>
        </>

    );
}

export default PickAndGoManager;